import { useState, useRef, useEffect } from 'react';
import { parseCurrencyInput, formatBRL } from '../../../utils/currency';

// Texto de edição: o número cru com vírgula decimal, sem milhar nem "R$". O parser
// lê isso sem ambiguidade ("1234,567" nunca vira milhar) e abrir/fechar a célula
// sem digitar nada devolve exatamente o mesmo valor.
function toEditText(v) {
  if (typeof v === 'number' && Number.isFinite(v)) return String(v).replace('.', ',');
  if (typeof v === 'string') return v;
  return '';
}

// type="text" de propósito: o input type="number" do NumberCell devolve '' para
// "1.234,56" / "-1.234,56" digitado em pt-BR, e a célula virava null em silêncio.
export default function CurrencyCell({ value, decimals, onChange, readOnly = false }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(toEditText(value));
  const [invalid, setInvalid] = useState(false);
  const inputRef = useRef(null);
  // A edição em curso já terminou (Enter, Escape ou blur): o blur que o navegador
  // pode disparar ao tirar o input da tela não salva nem cancela de novo.
  const doneRef = useRef(false);
  const flashRef = useRef(null);

  useEffect(() => { setText(toEditText(value)); }, [value]);
  useEffect(() => {
    if (editing && inputRef.current) { inputRef.current.focus(); inputRef.current.select(); }
  }, [editing]);
  useEffect(() => () => clearTimeout(flashRef.current), []);

  const startEditing = () => {
    if (readOnly) return;
    doneRef.current = false;
    setEditing(true);
  };

  // Texto que não é número não apaga o valor: volta ao anterior e marca a célula por um instante.
  const rejectInput = () => {
    setText(toEditText(value));
    setInvalid(true);
    clearTimeout(flashRef.current);
    flashRef.current = setTimeout(() => setInvalid(false), 1600);
  };

  const save = () => {
    if (doneRef.current) return;
    doneRef.current = true;
    setEditing(false);
    if (text === toEditText(value)) return; // nada digitado: não regrava nem reinterpreta o valor
    const parsed = parseCurrencyInput(text);
    if (parsed === null && text.trim() !== '') { rejectInput(); return; }
    // Grava já arredondado nas casas da coluna (passa pelo formatBRL, que valida as
    // casas): o que fica salvo é o que a célula mostra, e o total do rodapé, somado
    // em centavos, não diverge das linhas.
    const next = parsed === null ? null : parseCurrencyInput(formatBRL(parsed, decimals));
    if (next !== value) onChange(next);
    else setText(toEditText(value));
  };

  const cancel = () => {
    if (doneRef.current) return;
    doneRef.current = true;
    setText(toEditText(value));
    setEditing(false);
  };

  if (editing && !readOnly) {
    return (
      <input
        ref={inputRef}
        type="text"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={save}
        onKeyDown={(e) => {
          if (e.key === 'Enter') save();
          if (e.key === 'Escape') cancel();
        }}
        className="w-full h-full bg-transparent border-none outline-none text-text-primary text-sm px-2 text-right"
      />
    );
  }

  // Texto gravado por fora da tela (API) não vira dinheiro: aparece como veio, para dar pra ver e corrigir.
  const display = formatBRL(value, decimals) || (typeof value === 'string' ? value : '');

  return (
    <div
      onClick={startEditing}
      title={invalid ? 'Valor inválido: o anterior foi mantido' : (display || undefined)}
      className={`w-full h-full flex items-center justify-end px-2 text-sm text-text-primary whitespace-nowrap overflow-hidden transition-colors min-h-[40px] ${readOnly ? '' : 'cursor-pointer hover:bg-white/5'} ${invalid ? 'ring-1 ring-inset ring-danger' : ''}`}
    >
      {display || <span />}
    </div>
  );
}
